import React, { useState, useEffect } from 'react';
import { initializeAudioSystem, getAudioSystemInfo } from '../utils/mobileAudio';

interface AudioInitializerProps {
  children: React.ReactNode;
  autoInitialize?: boolean;
  showInitButton?: boolean;
}

const AudioInitializer: React.FC<AudioInitializerProps> = ({ 
  children, 
  autoInitialize = true,
  showInitButton = true 
}) => {
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [showInitPrompt, setShowInitPrompt] = useState(false);
  const [systemInfo, setSystemInfo] = useState<any>(null);

  useEffect(() => {
    // Check if we're on mobile and if audio system needs initialization
    const checkAudioSystem = () => {
      const info = getAudioSystemInfo();
      setSystemInfo(info);
      
      // Show init prompt for mobile users who haven't interacted yet
      if ((info as any).isMobile && !(info as any).userInteracted && showInitButton) {
        setShowInitPrompt(true);
      }
    };

    checkAudioSystem();

    // Set up event listeners for automatic initialization
    if (autoInitialize) {
      const handleUserInteraction = async () => {
        if (!audioInitialized) {
          const success = await initializeAudioSystem();
          if (success) {
            setAudioInitialized(true);
            setShowInitPrompt(false);
            console.log('✅ Audio system initialized automatically');
          }
        }
      };

      const events = ['touchstart', 'click', 'keydown'];
      events.forEach(event => {
        document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
      });

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleUserInteraction);
        });
      };
    }
  }, [autoInitialize, showInitButton, audioInitialized]);

  const handleManualInit = async () => {
    try {
      const success = await initializeAudioSystem();
      if (success) {
        setAudioInitialized(true);
        setShowInitPrompt(false);
        console.log('✅ Audio system initialized manually');
      } else {
        console.error('❌ Failed to initialize audio system');
      }
    } catch (error) {
      console.error('❌ Error initializing audio system:', error);
    }
  };

  return (
    <>
      {children}
      
      {/* Audio initialization prompt for mobile users */}
      {showInitPrompt && (
        <div className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg shadow-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 14.142M12 18a3 3 0 100-6 3 3 0 000 6z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold">Enable Audio</h4>
                <p className="text-xs opacity-90">
                  Tap to enable audio playback for Persian pronunciation
                </p>
              </div>
              <button
                onClick={handleManualInit}
                className="flex-shrink-0 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors"
                aria-label="Enable audio"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audio system status indicator (for development) */}
      {process.env.NODE_ENV === 'development' && systemInfo && (
        <div className="fixed top-4 right-4 z-50 bg-black/80 text-white text-xs p-2 rounded">
          <div>Mobile: {(systemInfo as any).isMobile ? '✅' : '❌'}</div>
          <div>iOS: {(systemInfo as any).isIOS ? '✅' : '❌'}</div>
          <div>Android: {(systemInfo as any).isAndroid ? '✅' : '❌'}</div>
          <div>User Interaction: {(systemInfo as any).userInteracted ? '✅' : '❌'}</div>
          <div>Audio Context: {(systemInfo as any).audioContextState || 'N/A'}</div>
          <div>Cache: {(systemInfo as any).cacheSize?.audioElements || 0} elements</div>
        </div>
      )}
    </>
  );
};

export default AudioInitializer; 